import {isBiologicsEnabled} from 'antibodies/util/biologicsCapability';
import BenchlingConfig from 'BenchlingConfig';
import {SliceComponentName} from 'constants/MapsConstants';
import {
  ANTIBODY_CDR_ANNOTATIONS,
  ANTIBODY_NUMBERING,
  PROTEIN_LIABILITY_SITES,
  STORED_ANTIBODY_NUMBERING,
  VisibilityOption,
  VisibilityOptions,
} from 'settings/visibility-options/VisibilityOptions';

import {CDRDefinition, NumberingScheme} from './AntibodyEngineeringConstants';

/**
 * Legacy Mol Bio antibody tools (numbering, CDR annotations, liability sites) are available by
 * visibility option: a map or alignment offers a tool only if its option is present. On Biologics
 * tenants, the sequence map removes the options for antibody-component entities (see
 * AaSequenceMapTabState), and alignments never include them (see
 * areLegacyAntibodyAlignmentToolsEnabled).
 */
function areLegacyAntibodyToolsEnabled(): boolean {
  return BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENGINEERING_TOOLS;
}

/**
 * Alignments can't tell which rows are Biologics antibody components, so on Biologics tenants they
 * omit the legacy tools entirely rather than risk overlaying them on Biologics-managed CDRs.
 */
export function areLegacyAntibodyAlignmentToolsEnabled(): boolean {
  return areLegacyAntibodyToolsEnabled() && !isBiologicsEnabled();
}

export const LEGACY_ANTIBODY_OPTION_NAMES = [
  ANTIBODY_NUMBERING.name,
  ANTIBODY_CDR_ANNOTATIONS.name,
  PROTEIN_LIABILITY_SITES.name,
];

const ANTIBODY_LAYER_OPTION_NAMES: Partial<Record<SliceComponentName, string>> = {
  [SliceComponentName.antibodyNumbering]: ANTIBODY_NUMBERING.name,
  [SliceComponentName.storedAntibodyNumbering]: STORED_ANTIBODY_NUMBERING.name,
  [SliceComponentName.antibodyCDRAnnotations]: ANTIBODY_CDR_ANNOTATIONS.name,
  [SliceComponentName.antibodyAlignmentCDRAnnotations]: ANTIBODY_CDR_ANNOTATIONS.name,
  [SliceComponentName.aaSequenceLiabilitySites]: PROTEIN_LIABILITY_SITES.name,
};

/**
 * Drops antibody layers whose visibility option is absent. Slice components treat a missing option as
 * always visible, and these layers' renderers require the option to exist.
 */
export function withoutUnavailableAntibodyLayers(
  components: Array<SliceComponentName>,
  visibilityOptions: VisibilityOptions
): Array<SliceComponentName> {
  return components.filter((component) => {
    const optionName = ANTIBODY_LAYER_OPTION_NAMES[component];
    return optionName == null || visibilityOptions.getOption(optionName) != null;
  });
}

/**
 * Removes the legacy antibody options from a collection.
 */
export function removeLegacyAntibodyOptions(visibilityOptions: VisibilityOptions): void {
  removeOptions(visibilityOptions, LEGACY_ANTIBODY_OPTION_NAMES);
}

/**
 * Removes the stored antibody numbering option from a collection.
 */
export function removeStoredAntibodyNumberingOption(visibilityOptions: VisibilityOptions): void {
  removeOptions(visibilityOptions, [STORED_ANTIBODY_NUMBERING.name]);
}

/**
 * Already-rendered layers keep a reference to their option, so each is hidden first; the `sync` type and
 * `noSave` keep that from propagating to linked collections or the user's saved preferences.
 */
function removeOptions(visibilityOptions: VisibilityOptions, names: Array<string>): void {
  const options = names
    .map((name) => visibilityOptions.getOption(name))
    .filter((option): option is VisibilityOption => option != null);
  if (options.length === 0) {
    return;
  }
  for (const option of options) {
    option.set('visible', false, {type: 'sync', noSave: true});
  }
  visibilityOptions.remove(options);
  visibilityOptions.makeLookup();
  visibilityOptions.trigger('change');
}

export function shouldShowNumbering(visibilityOptions: VisibilityOptions): boolean {
  return (
    areLegacyAntibodyToolsEnabled() &&
    Boolean(visibilityOptions.getOption(ANTIBODY_NUMBERING.name)?.get('visible'))
  );
}

export function shouldShowCDRs(visibilityOptions: VisibilityOptions): boolean {
  return (
    areLegacyAntibodyToolsEnabled() &&
    Boolean(visibilityOptions.getOption(ANTIBODY_CDR_ANNOTATIONS.name)?.get('visible'))
  );
}

export function shouldShowAntibodyTools(visibilityOptions: VisibilityOptions): boolean {
  return shouldShowNumbering(visibilityOptions) || shouldShowCDRs(visibilityOptions);
}

export function getAntibodyVisibilitySettings(
  visibilityOptions: VisibilityOptions
): [NumberingScheme | undefined, CDRDefinition | undefined] {
  if (!shouldShowAntibodyTools(visibilityOptions)) {
    return [undefined, undefined];
  }

  let numberingSetting;
  if (visibilityOptions.getOption(ANTIBODY_NUMBERING.name)?.get('visible')) {
    numberingSetting = visibilityOptions
      .getOption(ANTIBODY_NUMBERING.name)!
      .getSetting('numberingScheme')!
      .get('value')
      .toLowerCase();
  }

  let cdrSetting;
  if (visibilityOptions.getOption(ANTIBODY_CDR_ANNOTATIONS.name)?.get('visible')) {
    cdrSetting = visibilityOptions
      .getOption(ANTIBODY_CDR_ANNOTATIONS.name)!
      .getSetting('CDRDefinition')!
      .get('value')
      .toLowerCase();
  }
  return [numberingSetting, cdrSetting];
}

export function listenToAntibodyVisibilitySettings(
  visibilityOptions: VisibilityOptions,
  eventable: Backbone.Events,
  callbackFn: () => void
): void {
  const numberingOption = visibilityOptions.getOption(ANTIBODY_NUMBERING.name);
  const cdrOption = visibilityOptions.getOption(ANTIBODY_CDR_ANNOTATIONS.name);
  if (numberingOption == null || cdrOption == null) {
    return;
  }
  eventable.listenTo(numberingOption, 'change:visible', callbackFn);
  eventable.listenTo(cdrOption, 'change:visible', callbackFn);
  eventable.listenTo(numberingOption.getSetting('numberingScheme')!, 'change:value', callbackFn);
  eventable.listenTo(cdrOption.getSetting('CDRDefinition')!, 'change:value', callbackFn);
}
